/**
 * What went wrong, in plain words, with what to do about it.
 *
 * Sites built by AI builders (Lovable, Bolt, v0) are fixed by an AI that reads
 * the browser console, and by merchants who are not developers. A message here
 * says what happened and the one thing to do next, so either can fix it
 * without guessing. Each is logged once per page as `[Khaime] ...`.
 */

const YOUR_WEBSITE = 'Your website in Khaime';

export function plainError(status: number, code: string | undefined, serverMessage: string): string {
  switch (code) {
    case 'API_KEY_INVALID':
    case 'API_KEY_MISSING':
      return `${serverMessage} The key goes in khaime.init({ merchantId, apiKey }) and starts with pub_live_. Do not ask for a pk_ key: those are private and never go in a website.`;
    case 'API_KEY_RATE_LIMITED':
      return 'Too many requests from this visitor in a short time. Wait a minute and reload the page.';
    case 'AUTH_PERMISSION_DENIED':
      return `This website's address is not confirmed for this store yet. In ${YOUR_WEBSITE}, tap Yes on "Is this your website?", then reload. Lovable, Bolt and v0 preview addresses work without this.`;
    case 'BUSINESS_FEATURE_UNAVAILABLE':
      return `Selling from your own website is not switched on for this store. In ${YOUR_WEBSITE}, tap "Build my shop with Lovable" or "Copy for Lovable" once to switch it on.`;
    case 'BUSINESS_NOT_FOUND':
      return `This store was not found. Check merchantId in khaime.init matches the store ID on ${YOUR_WEBSITE}.`;
    case 'CART_OUT_OF_STOCK':
      return 'That product or option is sold out, so it was not added to the cart. Show it as sold out instead of offering Add to cart.';
    case 'PRODUCT_NOT_FOUND':
      return 'That product is not in this store any more. Load products with khaime.getProducts() instead of saving product ids in the website code.';
    case 'MULTICURRENCY_DISABLED':
      return 'This store sells in one currency only. Remove any currency picker; khaime.getCurrencies() says when one is allowed (canSwitch).';
    case 'PRICE_CURRENCY_MISMATCH':
      return 'The cart price did not match the store price. Show formatted_price exactly as given and never calculate prices in the website code.';
    default:
      if (status >= 500) return 'Khaime had a problem answering. Wait a minute and try again; nothing is wrong with the website code.';
      return serverMessage;
  }
}

const logged = new Set<string>();

/** Log a plain-word message once per page, so a console is not flooded on every retry. */
export function logPlainError(message: string): void {
  if (logged.has(message)) return;
  logged.add(message);
  console.error(`[Khaime] ${message}`);
}

/** Problems with the init options that would only show up later as a confusing failure. */
export function initProblems(options: { merchantId?: unknown; apiKey?: unknown }): string[] {
  const problems: string[] = [];
  const key = typeof options.apiKey === 'string' ? options.apiKey.trim() : '';
  if (!options.merchantId) {
    problems.push(`khaime.init is missing merchantId. It is the store ID on ${YOUR_WEBSITE}.`);
  }
  if (key.startsWith('pk_')) {
    problems.push(
      `This is a private partner key (pk_). Remove it from the website now: anyone can read website code. A website uses the store key that starts with pub_live_, on ${YOUR_WEBSITE}.`
    );
  } else if (key && !key.startsWith('pub_')) {
    problems.push(`The apiKey in khaime.init is not a Khaime store key. Copy the one that starts with pub_live_ from ${YOUR_WEBSITE}.`);
  }
  return problems;
}

export function resetPlainErrors(): void {
  logged.clear();
}
